import { media, stylesheet } from 'typestyle';
import { Button, Gray40, Tooltip } from '@traceair/tds';
import { ProjectData } from '../../../types';
import { GcpRequestData, useUploadStore } from '../../../../../../store/uploadStore';
import { useAppStore } from '../../../../../../store/appStore';
import { useDiscardGcpChanges } from './useDiscardGcpChanges';
import { useHandleGcpRequestSubmit } from './useHandleGcpRequestSubmit';
import { ScheduledFlight } from '../../../../../../types';
import { BREAKPOINT_SMALL } from '../../../../../layout/styles';
import { shouldConfirmGcpServiceHours } from '../GcpServiceHoursRow';

const css = stylesheet({
  buttonContainer: {
    borderTop: `solid 1px ${Gray40}`,
    padding: '12px 24px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'right',
    ...media({ maxWidth: BREAKPOINT_SMALL }, { paddingBottom: 12 }),
  },
  submitButton: {
    marginLeft: 'auto',
  },
});

interface Props extends ProjectData {
  gcpRequest: GcpRequestData;
  schedule?: ScheduledFlight;
}

export const GcpRequestSubmit = ({ customer, site, gcpRequest, schedule }: Props) => {
  const { hasGcpFormChanges, hasGcpUploadingFiles } = useUploadStore();
  const { pilot } = useAppStore();
  const handleDiscard = useDiscardGcpChanges(customer, site, gcpRequest);
  const handleSubmit = useHandleGcpRequestSubmit(customer, site, gcpRequest, schedule);

  const hasChanges = hasGcpFormChanges(customer.id, site.id);
  const areFilesLoading = hasGcpUploadingFiles(customer.id, site.id);

  // The /pilot endpoint returns no record for pilots without a UAS Pilots AT record (e.g.
  // self-serviced pilots) — their measurement files don't necessarily match the Emlid format
  // the parser expects, so don't let parse errors block their submission.
  const completedParseableUploads = gcpRequest.measurementUploads.filter((u) => u.isDone && u.parseResult != null);
  const hasMeasurementParseErrors =
    !!pilot?.id &&
    completedParseableUploads.length > 0 &&
    completedParseableUploads.every((u) => !!u.parseResult?.error);

  const needsServiceHoursConfirmation =
    !!pilot?.id && shouldConfirmGcpServiceHours(gcpRequest) && !gcpRequest.meta.areServiceHoursConfirmed;

  const disableSubmit =
    !hasChanges ||
    !gcpRequest.measurements.length ||
    areFilesLoading ||
    hasMeasurementParseErrors ||
    needsServiceHoursConfirmation;

  return (
    <div className={css.buttonContainer}>
      {!gcpRequest.meta.isSubmitting &&
        (gcpRequest.meta.prevMeasurements.length > 0 || hasChanges) &&
        !areFilesLoading && <Button style="secondary" text="Discard" onClick={handleDiscard} />}

      <Tooltip
        arrow
        open={areFilesLoading ? undefined : false}
        description="Please wait for the uploads to complete before submitting the form."
        placement="top"
        styleType="neutral-high"
        trigger="hover"
      >
        <Button
          disabled={disableSubmit}
          onClick={handleSubmit}
          loading={gcpRequest.meta.isSubmitting}
          text={gcpRequest.meta.prevMeasurements.length ? 'Update GCPs' : 'Send GCPs'}
          style="primary"
          className={css.submitButton}
        />
      </Tooltip>
    </div>
  );
};
